import apiClient from './authService';
import { messageVariableValuesService } from './messageVariableValuesService';

const normalizeVariableType = (type) => {
  if (typeof type === 'string' && type.trim()) {
    return type.trim();
  }
  return 'TEXT';
};

const toVariablesObject = (variablesSource) => {
  if (!variablesSource) return {};

  // Already object format: { "{{1}}": { type, value } }
  if (!Array.isArray(variablesSource) && typeof variablesSource === 'object') {
    return Object.entries(variablesSource).reduce((acc, [expression, data]) => {
      if (!expression) return acc;
      acc[expression] = {
        type: normalizeVariableType(data?.type),
        value: data?.value || ''
      };
      return acc;
    }, {});
  }

  // Array format: [{ variableExpression, type, value }]
  if (Array.isArray(variablesSource)) {
    return variablesSource.reduce((acc, variable) => {
      const expression = variable?.variableExpression;
      if (!expression) return acc;
      acc[expression] = {
        type: normalizeVariableType(variable?.type),
        value: variable?.value || ''
      };
      return acc;
    }, {});
  }

  return {};
};

const mapVariablesForRequest = (node) => {
  const variablesObject = toVariablesObject(node?.data?.variables);
  return Object.entries(variablesObject).map(([variableExpression, data]) => ({
    variableExpression,
    value: data?.value || '',
    type: normalizeVariableType(data?.type)
  }));
};

const mapNodesForConfigurationRequest = (nodes = []) => {
  return nodes.map((node, index) => ({
    nodeKey: node.id,
    nodeLabel: node.data?.label || '',
    nodeType: node.data?.type === 'template' ? 'TEMPLATE' : 'FREE_TEXT',
    content: node.data?.content || '',
    templateId: node.data?.templateId || null,
    positionX: node.position?.x ?? 0,
    positionY: node.position?.y ?? 0,
    isStartNode: node.data?.isStart || false,
    isEndNode: false,
    nodeOrder: index,
    variables: mapVariablesForRequest(node),
    flowConfig: node.data?.flowConfig || null
  }));
};

const mapEdgesForConfigurationRequest = (edges = []) => {
  return edges.map((edge, index) => ({
    sourceNodeKey: edge.source,
    targetNodeKey: edge.target,
    edgeLabel: edge.data?.label || '',
    edgeType: 'DEFAULT',
    conditionExpression: null,
    edgeOrder: index,
    sourceHandle: edge.sourceHandle || null,
    targetHandle: edge.targetHandle || null
  }));
};

const normalizeConfigurationResponse = (responseData) => ({
  nodes: Array.isArray(responseData?.nodes) ? responseData.nodes : [],
  edges: Array.isArray(responseData?.edges) ? responseData.edges : []
});

// Helper function to map BotResponse to FlowResponse format for component compatibility
const mapBotToFlow = (bot) => {
  if (!bot) return bot;
  return {
    flowId: bot.botId,
    flowName: bot.botName,
    name: bot.botName, // Some components use 'name'
    status: bot.botStatus,
    flowStatus: bot.botStatus, // Some components use 'flowStatus'
    flowCategoryCode: bot.botCategoryCode,
    categoryCode: bot.botCategoryCode,
    description: bot.description,
    botCategoryDescription: bot.botCategoryDescription,
    version: bot.version,
    isActive: bot.isActive,
    isPublic: bot.isPublic,
    createdAt: bot.createdAt,
    updatedAt: bot.updatedAt,
    publishedAt: bot.publishedAt,
    nodeCount: bot.nodeCount,
    edgeCount: bot.edgeCount,
    nodes: bot.nodes,
    edges: bot.edges
  };
};

// Helper function to map array of bots to flows
const mapBotsToFlows = (bots) => {
  if (Array.isArray(bots)) {
    return bots.map(mapBotToFlow);
  }
  if (bots && bots.content) {
    // Handle paginated response
    return {
      ...bots,
      content: bots.content.map(mapBotToFlow)
    };
  }
  return bots;
};

const botService = {
  // Get all bots
  getBots: async (page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') => {
    try {
      const response = await apiClient.get('/bots', {
        params: { page, size, sortBy, sortDir }
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching bots:', error);
      throw error;
    }
  },

  // Get flow by ID
  getBot: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/${botId}`);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching bot:', error);
      throw error;
    }
  },

  // Create new flow
  createBot: async (botData) => {
    console.log('botService.createBot called with:', botData);
    try {
      const requestData = {
        botName: botData.name,
        description: botData.description || '',
        botStatus: botData.status || '10001', // Use the new status code
        botCategoryCode: botData.categoryCode || null,
        isPublic: botData.isPublic || false,
        nodes: [],
        edges: []
      };
      
      const response = await apiClient.post('/bots', requestData);
      console.log('Created new bot:', response.data);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating bot:', error);
      throw error;
    }
  },

  // Update bot
  updateBot: async (botId, botData) => {
    try {
      const requestData = {
        botName: botData.name,
        description: botData.description || '',
        botStatus: botData.status,
        botCategoryCode: botData.categoryCode || null,
        isPublic: botData.isPublic || false
      };
      
      const response = await apiClient.patch(`/bots/${botId}`, requestData);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating bot:', error);
      throw error;
    }
  },

  // Delete bot
  deleteBot: async (botId) => {
    try {
      const response = await apiClient.delete(`/bots/${botId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting bot:', error);
      throw error;
    }
  },

  // Save bot configuration
  saveBotConfiguration: async (botId, nodes, edges) => {
    try {
      const requestData = {
        nodes: mapNodesForConfigurationRequest(nodes),
        edges: mapEdgesForConfigurationRequest(edges)
      };

      const response = await apiClient.put(`/bots/${botId}/configuration`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error saving bot configuration:', error);
      throw error;
    }
  },

  // Get bot configuration
  getBotConfiguration: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/${botId}/configuration`);
      const normalizedConfig = normalizeConfigurationResponse(response.data);
      
      // Convert backend format to ReactFlow format
      const nodes = await Promise.all(
        normalizedConfig.nodes.map(async (node) => {
          // Fetch all variables for this node from MessageVariableValues service
          let variables = {};
          if (node.nodeId) {
            try {
              const variablesResponse = await messageVariableValuesService.getVariablesByEntityId(
                'BOT_NODE',
                node.nodeId
              );
              // Convert array to object format: { "{{1}}": { type: "TEXT", value: "..." }, ... }
              if (variablesResponse && Array.isArray(variablesResponse) && variablesResponse.length > 0) {
                variables = variablesResponse.reduce((acc, variable) => {
                  acc[variable.variableExpression] = {
                    type: variable.type,
                    value: variable.value || ''
                  };
                  return acc;
                }, {});
              }
            } catch (error) {
              console.warn(`Could not fetch variables for node ${node.nodeId}:`, error.message);
            }
          }
          
          // Fallback: use single variable from response if available (backward compatibility)
          // This handles cases where API is not available or node doesn't have variables yet
          if (Object.keys(variables).length === 0 && node.variableExpression && node.variableType) {
            variables = {
              [node.variableExpression]: {
                type: normalizeVariableType(node.variableType),
                value: node.value || ''
              }
            };
          }
          
          return {
            id: node.nodeKey,
            type: 'flowNode',
            position: { x: node.positionX, y: node.positionY },
            data: {
              label: node.nodeLabel,
              type: node.nodeType === 'TEMPLATE' ? 'template' : 'freeText',
              content: node.content || '',
              templateId: node.templateId,
              isStart: node.isStartNode,
              flowConfig: node.flowConfig || null,
              // Store all variables in node data
              variables: variables
            }
          };
        })
      );

      const edges = normalizedConfig.edges.map(edge => ({
        id: `edge-${edge.sourceNodeKey}-${edge.targetNodeKey}`,
        source: edge.sourceNodeKey,
        target: edge.targetNodeKey,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
        type: 'flowEdge',
        data: {
          label: edge.edgeLabel || 'Click to edit',
          id: `edge-${edge.sourceNodeKey}-${edge.targetNodeKey}`
        }
      })) || [];

      return { nodes, edges };
    } catch (error) {
      console.error('Error fetching bot configuration:', error);
      throw error;
    }
  },

  // Publish bot
  publishBot: async (botId) => {
    try {
      const requestData = {
        botStatus: '10002'
      };
      const response = await apiClient.patch(`/bots/${botId}`, requestData);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error publishing bot:', error);
      throw error;
    }
  },

  // Unpublish bot
  unpublishBot: async (botId) => {
    try {
        const requestData = {
          botStatus: '10001'
      };
      const response = await apiClient.patch(`/bots/${botId}`, requestData);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error unpublishing bot:', error);
      throw error;
    }
  },

  // Get active session count for a bot
  getActiveSessionCount: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/${botId}/active-sessions/count`);
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching active session count:', error);
      throw error;
    }
  },

  // Get bots by status
  getBotsByStatus: async (status) => {
    try {
      const response = await apiClient.get(`/bots/status/${status}`);
      //return mapBotsToFlows(response.data) || [];
      return response.data.content || [];
    } catch (error) {
      console.error('Error fetching bots by status:', error);
      throw error;
    }
  },

  // Search bots
  searchBots: async (filters = {}) => {
    try {
      const response = await apiClient.get('/bots/search', {
        params: filters
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching bots:', error);
      throw error;
    }
  },

  // Duplicate bot
  duplicateBot: async (botId, newName) => {
    try {
      const response = await apiClient.post(`/bots/${botId}/duplicate?newbotName=${encodeURIComponent(newName)}`);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error duplicating bot:', error);
      throw error;
    }
  },

  // Export bot
  exportBot: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/${botId}/export`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting bot:', error);
      throw error;
    }
  },

  // Import bot
  importBot: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/bots/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error importing bot:', error);
      throw error;
    }
  },

  // Get collection bots (public bots with pagination)
  getCollectionBots: async (page = 0, size = 12, sortBy = 'createdAt', sortDir = 'desc') => {
    try {
      const response = await apiClient.get('/bots/public', {
        params: { page, size, sortBy, sortDir }
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching collection bots:', error);
      throw error;
    }
  },

  // Get my list bots (user bots with pagination)
  getMyListBots: async (page = 0, size = 12, sortBy = 'createdAt', sortDir = 'desc') => {
    try {
      const response = await apiClient.get('/bots', {
        params: { page, size, sortBy, sortDir }
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching my list bots:', error);
      throw error;
    }
  },

  // Search public bots
  searchPublicBots: async (searchTerm) => {
    try {
      const response = await apiClient.get('/bots/search', {
        params: { name: searchTerm }
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching public bots:', error);
      throw error;
    }
  },

  // Add bot to customer's list
  addBotToCustomer: async (botId) => {
    try {
      const response = await apiClient.post(`/bots/${botId}/add-to-customer`);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding bot to customer:', error);
      throw error;
    }
  },

  // ==================== ADMIN METHODS ====================

  // Create admin bot
  createAdminBot: async (botData) => {
    console.log('botService.createAdminBot called with:', botData);
    try {
      const requestData = {
        botName: botData.name,
        description: botData.description || '',
        botStatus: botData.status || '10001', // Use the new status code
        botCategoryCode: botData.categoryCode || null,
        isPublic: true, // Admin bots are always public
        nodes: [],
        edges: []
      };
      
      const response = await apiClient.post('/bots/admin', requestData);
      console.log('Created new admin bot:', response.data);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating admin bot:', error);
      throw error;
    }
  },

  // Get admin bots (public bots)
  getAdminBots: async (page = 0, size = 12, sortBy = 'createdAt', sortDir = 'desc') => {
    try {
      const response = await apiClient.get('/bots/admin', {
        params: { page, size, sortBy, sortDir }
      });
      //return mapBotsToFlows(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin bots:', error);
      throw error;
    }
  },

  // Get admin bot by ID
  getAdminBot: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/admin/${botId}`);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin bot:', error);
      throw error;
    }
  },

  // Update admin bot
  updateAdminBot: async (botId, botData) => {
    try {
      const requestData = {
        botName: botData.name,
        description: botData.description || '',
        botStatus: botData.status,
        botCategoryCode: botData.categoryCode || null,
        isPublic: true // Admin flows are always public
      };
      
      const response = await apiClient.patch(`/bots/admin/${botId}`, requestData);
      //return mapBotToFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating admin bot:', error);
      throw error;
    }
  },

  // Delete admin bot
  deleteAdminBot: async (botId) => {
    try {
      const response = await apiClient.delete(`/bots/admin/${botId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting admin bot:', error);
      throw error;
    }
  },

  // Get admin bot configuration
  getAdminBotConfiguration: async (botId) => {
    try {
      const response = await apiClient.get(`/bots/admin/${botId}/configuration`);
      const normalizedConfig = normalizeConfigurationResponse(response.data);
      
      // Convert backend format to ReactFlow format
      const nodes = normalizedConfig.nodes.map(node => ({
        id: node.nodeKey,
        type: 'botNode',
        position: { x: node.positionX, y: node.positionY },
        data: {
          label: node.nodeLabel,
          type: node.nodeType === 'TEMPLATE' ? 'template' : 'freeText',
          content: node.content || '',
          templateId: node.templateId,
          isStart: node.isStartNode,
          variables: node.variableExpression
            ? {
                [node.variableExpression]: {
                  type: normalizeVariableType(node.variableType),
                  value: node.value || ''
                }
              }
            : {}
        }
      }));

      const edges = normalizedConfig.edges.map(edge => ({
        id: `edge-${edge.sourceNodeKey}-${edge.targetNodeKey}`,
        source: edge.sourceNodeKey,
        target: edge.targetNodeKey,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
        type: 'botEdge',
        data: {
          label: edge.edgeLabel || 'Click to edit',
          id: `edge-${edge.sourceNodeKey}-${edge.targetNodeKey}`
        }
      }));

      return { nodes, edges };
    } catch (error) {
      console.error('Error fetching admin bot configuration:', error);
      throw error;
    }
  },

  // Save admin bot configuration
  saveAdminBotConfiguration: async (botId, nodes, edges) => {
    try {
      const requestData = {
        nodes: mapNodesForConfigurationRequest(nodes),
        edges: mapEdgesForConfigurationRequest(edges)
      };

      const response = await apiClient.put(`/bots/admin/${botId}/configuration`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error saving admin bot configuration:', error);
      throw error;
    }
  }
};

export { botService };
